import { useState } from "react";
import { Copy, Mail, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { api, formatApiError } from "../../lib/api";
import { CONTACT } from "../../data/content";

export const EmailWithCopy = ({ email = CONTACT.email, className = "", testId = "email-copy" }) => {
  const copy = async () => {
    try {
      await navigator.clipboard?.writeText(email);
      toast.success("Adresse e-mail copiée", { description: email });
    } catch {
      toast.info("Adresse e-mail", { description: email });
    }
  };
  return (
    <span className={`inline-flex flex-wrap items-center gap-1.5 ${className}`}>
      <span data-testid={testId}>{email}</span>
      <button
        type="button"
        onClick={copy}
        data-testid={`${testId}-btn`}
        aria-label="Copier l'adresse e-mail"
        className="inline-flex items-center gap-1 rounded-md border border-current/40 px-1.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide opacity-75 transition-opacity hover:opacity-100"
      >
        <Copy className="h-3 w-3" /> Copier
      </button>
    </span>
  );
};

const EMPTY = { nom: "", email: "", tel: "", message: "", website: "" };

export const ContactFormDialog = ({ open, onOpenChange, subject }) => {
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const close = (v) => {
    onOpenChange(v);
    if (!v) {
      setSent(false);
      setForm(EMPTY);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/contact", { ...form, sujet: subject });
      setSent(true);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "w-full rounded-xl border border-[#2E2A22]/15 bg-white px-4 py-2.5 text-sm text-[#2E2A22] outline-none transition-colors focus:border-[#A66E08] focus:ring-2 focus:ring-[#A66E08]/20";

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent data-testid="contact-form-dialog" className="max-w-md bg-[#FBF7EF] text-[#2E2A22] border-[#2E2A22]/10">
        {sent ? (
          <div className="py-6 text-center" data-testid="contact-form-success">
            <CheckCircle2 className="mx-auto h-12 w-12 text-[#A66E08]" strokeWidth={1.5} />
            <h3 className="mt-4 font-serif text-2xl">Merci, votre message a bien été envoyé.</h3>
            <p className="mt-2 text-sm text-[#6B6355]">
              L'association Plus Belle La Vie 971 vous répondra à l'adresse indiquée.
            </p>
            <button
              type="button"
              onClick={() => close(false)}
              data-testid="contact-form-close"
              className="mt-6 rounded-full bg-[#2E2A22] px-7 py-3 text-sm font-semibold text-[#FBF7EF] transition-colors hover:bg-[#A66E08]"
            >
              Fermer
            </button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="font-serif text-2xl font-normal text-[#2E2A22]">Écrire à l'association</DialogTitle>
              <DialogDescription className="text-sm text-[#6B6355]">
                Votre message sera transmis à l'association Plus Belle La Vie 971, qui vous répondra par e-mail.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-3">
              <p
                data-testid="contact-form-sujet"
                className="inline-flex items-center gap-2 rounded-full bg-[#A66E08]/10 px-3.5 py-1.5 text-xs font-semibold text-[#A66E08]"
              >
                <Mail className="h-3.5 w-3.5" /> Sujet : {subject}
              </p>
              <input
                type="text"
                value={form.website}
                onChange={set("website")}
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                className="hidden"
              />
              <div>
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-[#6B6355]">Nom *</label>
                <input data-testid="contact-form-nom" required value={form.nom} onChange={set("nom")} className={inputCls} placeholder="Votre nom" />
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-[#6B6355]">E-mail *</label>
                <input data-testid="contact-form-email" required type="email" value={form.email} onChange={set("email")} className={inputCls} placeholder="vous@exemple.com" />
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-[#6B6355]">Téléphone (facultatif)</label>
                <input data-testid="contact-form-tel" type="tel" value={form.tel} onChange={set("tel")} className={inputCls} placeholder="06 90 …" />
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-[#6B6355]">Message *</label>
                <textarea data-testid="contact-form-message" required rows={4} value={form.message} onChange={set("message")} className={inputCls} placeholder="Votre message…" />
              </div>
              <button
                type="submit"
                disabled={sending}
                data-testid="contact-form-submit"
                className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#A66E08] px-7 py-3 text-sm font-semibold text-white transition-colors hover:bg-[#C6870F] disabled:opacity-60"
              >
                {sending && <Loader2 className="h-4 w-4 animate-spin" />}
                Envoyer mon message
              </button>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export const ContactButton = ({ subject, className = "", children, testId }) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} data-testid={testId} className={`text-left ${className}`}>
        {children}
      </button>
      <ContactFormDialog open={open} onOpenChange={setOpen} subject={subject} />
    </>
  );
};
